
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ChartInfoShort struct
 */
class ChartInfoShort {
  /**
   * Initialize struct
   * @param {!UUID=} dashboardId
   * @param {!UUID=} chartId
   * @param {!string=} symbol
   * @param {!number=} version
   * @constructor
   */
  constructor (dashboardId = new UUID(), chartId = new UUID(), symbol = '', version = new UInt64(0, 0)) {
    this.DashboardId = dashboardId
    this.ChartId = chartId
    this.Symbol = symbol
    this.Version = version
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ChartInfoShort}
   * @param {!ChartInfoShort} other Other struct
   * @returns {!ChartInfoShort} This struct
   */
  copy (other) {
    if (other.DashboardId != null) {
      this.DashboardId = new UUID(other.DashboardId)
    } else {
      this.DashboardId = null
    }
    if (other.ChartId != null) {
      this.ChartId = new UUID(other.ChartId)
    } else {
      this.ChartId = null
    }
    if (other.Symbol != null) {
      this.Symbol = other.Symbol
    } else {
      this.Symbol = null
    }
    if (other.Version != null) {
      this.Version = UInt64.fromNumber(other.Version)
    } else {
      this.Version = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ChartInfoShort}
   * @returns {!ChartInfoShort} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ChartInfoShortModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ChartInfoShortModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ChartInfoShort}
   * @param {!ChartInfoShort} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ChartInfoShort)) {
      throw new TypeError('Instance of ChartInfoShort is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ChartInfoShort}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DashboardId: ((this.DashboardId != null) ? this.DashboardId.toString() : null),
      ChartId: ((this.ChartId != null) ? this.ChartId.toString() : null),
      Symbol: ((this.Symbol != null) ? this.Symbol : null),
      Version: ((this.Version != null) ? this.Version.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ChartInfoShort.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ChartInfoShort} other Object value
   * @returns {!ChartInfoShort} Created struct
   */
  static fromObject (other) {
    return new ChartInfoShort().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ChartInfoShort}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ChartInfoShort.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ChartInfoShort}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5189
  }
}

export { ChartInfoShort };

/**
 * Fast Binary Encoding ChartInfoShort field model
 */
class FieldModelChartInfoShort extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DashboardId = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.ChartId = new fbe.FieldModelUUID(buffer, this.DashboardId.fbeOffset + this.DashboardId.fbeSize)
    this.Symbol = new fbe.FieldModelString(buffer, this.ChartId.fbeOffset + this.ChartId.fbeSize)
    this.Version = new fbe.FieldModelUInt64(buffer, this.Symbol.fbeOffset + this.Symbol.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelChartInfoShort}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelChartInfoShort}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DashboardId.fbeSize + this.ChartId.fbeSize + this.Symbol.fbeSize + this.Version.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelChartInfoShort}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DashboardId.fbeExtra + this.ChartId.fbeExtra + this.Symbol.fbeExtra + this.Version.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelChartInfoShort}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelChartInfoShort.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelChartInfoShort}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5189
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelChartInfoShort}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelChartInfoShort}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DashboardId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DashboardId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardId.fbeSize

    if ((fbeCurrentSize + this.ChartId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ChartId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartId.fbeSize

    if ((fbeCurrentSize + this.Symbol.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Symbol.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Symbol.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Version.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelChartInfoShort}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelChartInfoShort}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelChartInfoShort}
   * @param {!ChartInfoShort} fbeValue Default value, defaults is new ChartInfoShort()
   * @returns {!ChartInfoShort} ChartInfoShort value
   */
  get (fbeValue = new ChartInfoShort()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelChartInfoShort}
   * @param {!ChartInfoShort} fbeValue ChartInfoShort value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DashboardId.fbeSize) <= fbeStructSize) {
      fbeValue.DashboardId = this.DashboardId.get()
    } else {
      fbeValue.DashboardId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardId.fbeSize

    if ((fbeCurrentSize + this.ChartId.fbeSize) <= fbeStructSize) {
      fbeValue.ChartId = this.ChartId.get()
    } else {
      fbeValue.ChartId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartId.fbeSize

    if ((fbeCurrentSize + this.Symbol.fbeSize) <= fbeStructSize) {
      fbeValue.Symbol = this.Symbol.get()
    } else {
      fbeValue.Symbol = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Symbol.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) <= fbeStructSize) {
      fbeValue.Version = this.Version.get()
    } else {
      fbeValue.Version = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelChartInfoShort}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelChartInfoShort}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelChartInfoShort}
   * @param {!ChartInfoShort} fbeValue ChartInfoShort value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelChartInfoShort}
   * @param {ChartInfoShort} fbeValue ChartInfoShort value
   */
  setFields (fbeValue) {
    this.DashboardId.set(fbeValue.DashboardId)
    this.ChartId.set(fbeValue.ChartId)
    this.Symbol.set(fbeValue.Symbol)
    this.Version.set(fbeValue.Version)
  }
}

export { FieldModelChartInfoShort };

/**
 * Fast Binary Encoding ChartInfoShort model
 */
class ChartInfoShortModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelChartInfoShort(this.buffer, 4)
  }

  /**
   * Get the ChartInfoShort model
   * @this {!ChartInfoShortModel}
   * @returns {!FieldModelChartInfoShort} model ChartInfoShort model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ChartInfoShortModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ChartInfoShortModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ChartInfoShortModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ChartInfoShortModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelChartInfoShort.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ChartInfoShortModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ChartInfoShortModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ChartInfoShortModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ChartInfoShortModel}
   * @param {!ChartInfoShort} value ChartInfoShort value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ChartInfoShortModel}
   * @param {!ChartInfoShort} value ChartInfoShort value, defaults is new ChartInfoShort()
   * @return {!object} Deserialized ChartInfoShort value and its size
   */
  deserialize (value = new ChartInfoShort()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ChartInfoShort(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ChartInfoShort(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ChartInfoShortModel}
   * @param {!number} prev Previous ChartInfoShort model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ChartInfoShortModel };
